import {
  Alert,
  AlertTitle,
  Box,
  Paper,
  Popover,
  Skeleton,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { useParams } from "react-router-dom";

import { CommentsBox } from "@/shared/components";
import {
  AnnotationViewFilters,
  AnnotationsMenu,
} from "@/shared/components/Annotations";
import { FileNode } from "@/shared/domain/files/filesystem";
import { isImage } from "@/shared/domain/files/fileTypes";
import {
  useFile,
  useAnnotationData,
  useAnnotationFiles,
  useAllDirectoryFiles,
} from "@/shared/domain/files/hooks";

import { FileContent } from "./FileContent";
import { FileHeader } from "./FileHeader";

interface PopoverController {
  element: HTMLButtonElement | null;
  component: "annotations" | "details" | null;
}

export function File() {
  const { fileId } = useParams();

  const theme = useTheme();

  const fileRecordQuery = useFile(fileId);

  const [popoverAnchorEl, setPopoverAnchorEl] =
    React.useState<PopoverController>();

  const [annotationFile, setAnnotationFile] = React.useState<
    FileNode | undefined
  >();

  const [annotationViewFilters, setAnnotationViewFilters] =
    React.useState<AnnotationViewFilters>({
      confidenceThreshold: { percent: 0, show: false },
      labels: { show: true },
      boundingBox: { show: true },
      segmentation: { show: false },
    });

  const [datasetId, datasetOrgId] = React.useMemo(() => {
    if (!fileRecordQuery.data) {
      return [];
    }
    return [fileRecordQuery.data.association_id, fileRecordQuery.data.org_id];
  }, [fileRecordQuery.data]);

  const directoryPath = fileRecordQuery?.data?.relative_path
    .split("/")
    .slice(0, -1)
    .join("/");

  const allFilesInDirQuery = useAllDirectoryFiles(
    datasetId,
    directoryPath,
    datasetOrgId,
  );

  const annotationsDataQuery = useAnnotationData(annotationFile?.file);

  const annotationFilesQuery = useAnnotationFiles(datasetId, directoryPath);

  React.useEffect(() => {
    if (annotationFilesQuery.isSuccess) {
      setAnnotationFile(annotationFilesQuery.data[0]);
    }
  }, [annotationFilesQuery.data, annotationFilesQuery.isSuccess]);

  let fileFocalContent;

  const header = (
    <FileHeader
      datasetId={datasetId}
      fileRecord={fileRecordQuery.data}
      siblingFiles={allFilesInDirQuery.data}
      isContentImage={
        fileRecordQuery.isSuccess
          ? isImage(fileRecordQuery.data.relative_path) !== null
          : false
      }
      handlePopover={(
        component: "annotations" | "details" | null,
        element: HTMLButtonElement | null,
      ) => {
        setPopoverAnchorEl({
          component: component,
          element: element,
        });
      }}
    />
  );
  if (fileRecordQuery.isSuccess) {
    // Show file visualization UI
    fileFocalContent = (
      <>
        {header}
        <Box
          sx={{
            display: "flex",
            width: "100%",
            mt: theme.spacing(2),
            minHeight: "300px",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxHeight: "75vh",
              overflow: "scroll",
            }}
          >
            <FileContent
              fileRecord={fileRecordQuery.data}
              annotationData={annotationsDataQuery.data}
              annotationViewFilters={annotationViewFilters}
            />
          </Box>
        </Box>
        <Box
          sx={{
            marginTop: theme.spacing(2),
            pl: theme.spacing(1),
            pr: theme.spacing(1),
          }}
        >
          <CommentsBox
            entityType={"file"}
            entityId={fileRecordQuery.data.file_id}
          />
        </Box>
      </>
    );
  } else {
    fileFocalContent = (
      <>
        {header}
        <Skeleton animation="wave" variant="rounded" height={"400px"} />
      </>
    );
  }

  return (
    <>
      {fileRecordQuery.isError ? (
        <Alert severity="error">
          <AlertTitle>Could not load file</AlertTitle>
          {fileRecordQuery.error.message}
        </Alert>
      ) : (
        <Box>{fileFocalContent}</Box>
      )}
      <Popover
        id={"popover-file-annotations"}
        open={Boolean(popoverAnchorEl?.element)}
        anchorEl={popoverAnchorEl?.element}
        onClose={() =>
          setPopoverAnchorEl({
            component: popoverAnchorEl?.component || null,
            element: null,
          })
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: -15,
          horizontal: "center",
        }}
        marginThreshold={5}
        slotProps={{
          paper: {
            sx: {
              overflow: "unset",
            },
          },
        }}
      >
        <Box
          component={Paper}
          elevation={24}
          sx={{
            maxWidth: "400px",
            minWidth: "150px",
            padding: theme.spacing(1.5),
          }}
        >
          {popoverAnchorEl?.component === "annotations" && (
            <AnnotationsMenu
              annotationFiles={annotationFilesQuery.data}
              annotationFile={annotationFile}
              annotationDataLoading={annotationsDataQuery.isLoading}
              annotationDataError={annotationsDataQuery.error ?? undefined}
              annotationViewFilters={annotationViewFilters}
              onViewFiltersChanged={(filters) =>
                setAnnotationViewFilters(filters)
              }
              onAnnotationFileSelected={(file) => {
                if (!file) {
                  setAnnotationFile(undefined);
                } else {
                  setAnnotationFile(file);
                }
              }}
              inline={false}
            />
          )}
        </Box>
      </Popover>
    </>
  );
}
